import * as allPermissions from '@mm/constants/facebook-permissions';
import { UNPROCESSABLE_ENTITY } from '@mm/constants/status-codes';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import {
  InstagramConnectModalProps,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { ReactComponent as InstagramIcon } from 'assets/instagram.svg';
import instagramCrystal from 'assets/instagram-crystal.png';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { StepperComponent } from '../../../dashboard/pages/SignupOnboarding/pages/NewUserInfoFlow/components/SetCompanyInfo/Stepper/StepperComponent';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import { trackWebAppGAEvent } from '../../helpers/plugins/website-google-analytics';
import {
  CloseModalProps,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../modal-helpers';
import './InstagramConnectModal.pcss';

export const IG_BETA_SIGNUP_GA_EVENT = 'ig_beta_signup';

type StepType = {
  body: JSX.Element | null;
  footer: string | null;
  onClick: () => void;
  disabled?: boolean;
  hideHeader?: boolean;
  hideStepper?: boolean;
};

const InstagramConnectModal = (
  props: CloseModalProps & InstagramConnectModalProps,
) => {
  const {
    closeModal,
    startStep,
    showCongratulations,
    eventName,
    onSuccessInstagramConnect,
    otherFacebookPage,
  } = props;
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const facebookPage = otherFacebookPage
    ? otherFacebookPage
    : activeFacebookPage;
  const dispatch = useDispatch();
  const { activateIG } = useInstagramCRUDActions();
  const permissions = allPermissions.getArrayOfPermissions(
    allPermissions.INSTAGRAM_PERMISSIONS,
  );

  const trackEventName = eventName ?? IG_BETA_SIGNUP_GA_EVENT;

  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const history = useHistory();

  const modalActions = useModalActions();
  const chatbotCrudActions = useChatbotCRUDActions();
  const promoterCRUDActions = usePromoterCRUDActions();
  const { retrieveAll: retrieveChatbotsByPage } = useChatbotCRUDActions();

  const [step, setStep] = useState(startStep || 0);
  const [firstChecked, setFirstChecked] = useState(false);
  const [secondChecked, setSecondChecked] = useState(false);
  const [thirdChecked, setThirdChecked] = useState(false);

  const allChecked = firstChecked && secondChecked && thirdChecked;
  const chatbotId = otherFacebookPage
    ? otherFacebookPage.activeBotId || otherFacebookPage.firstBotId
    : activeChatbotId;

  const retrieveBots = async () => {
    if (facebookPage && !facebookPage.activeBotId) {
      await retrieveChatbotsByPage(facebookPage.id);
    }
  };
  const skipFetchActivePage = otherFacebookPage
    ? otherFacebookPage?.id !== activeFacebookPage?.id
    : false;
  const getAndDeactivateIGWecomer = async () => {
    const response = await promoterCRUDActions.retrieveAll(
      chatbotId,
      'instagram_welcomer',
    );
    if (response?.length) {
      const { id } = response[0];
      if (id) {
        await promoterCRUDActions.update(chatbotId, {
          id,
          active: false,
        });
      }
    }
  };

  useEffect(() => {
    if (step === 3) {
      getAndDeactivateIGWecomer();
    }
  }, [step]);

  const StepOne = () => {
    return (
      <div className="instagram-connect-modal__step">
        <div className="instagram-connect-modal__step--intro">
          Wait!
          <br />
          Before you connect your account, check these three steps:
        </div>
        <div className="instagram-connect-modal__step--items">
          <div className="instagram-connect-modal__step--item">
            <input
              type="checkbox"
              checked={firstChecked}
              onChange={() => setFirstChecked(!firstChecked)}
            />
            <div>
              Your Instagram account has been converted to a business account.
              <a
                href="https://www.facebook.com/business/help/502981923235522"
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-connect-modal__step-item-link"
              >
                See the instructions
              </a>
            </div>
          </div>
          <div className="instagram-connect-modal__step--item">
            <input
              type="checkbox"
              checked={secondChecked}
              onChange={() => setSecondChecked(!secondChecked)}
            />
            <div>
              You have enabled access to IG partner messages on your Instagram
              account.{' '}
              <a
                href="https://mobilemonkey.com/help/article/vq42z251fw-enable-access-to-instagram-partner-messages"
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-connect-modal__step-item-link"
              >
                See the instructions
              </a>
            </div>
          </div>
          <div className="instagram-connect-modal__step--item">
            <input
              type="checkbox"
              checked={thirdChecked}
              onChange={() => setThirdChecked(!thirdChecked)}
            />
            <div>
              Your Instagram account has been linked to a business page on
              Facebook.{' '}
              <a
                href="https://www.facebook.com/business/help/345675946300334?id=419087378825961"
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-connect-modal__step-item-link"
              >
                See the instructions
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StepTwo = () => {
    return (
      <div className="instagram-connect-modal__step">
        <div className="instagram-connect-modal__step--intro">
          Time to connect your Instagram account to MobileMonkey!
        </div>
        <div className="instagram-connect-modal__step--text">
          <ol>
            <li>You’ll be asked to sign into your Facebook account first</li>
            <li>
              After that, select the Instagram account you’d like to connect and
              confirm pages & permissions
            </li>
            <li className="instagram-connect-modal__step-text-bold">
              Make sure ALL permissions are enabled.
            </li>
          </ol>
        </div>
      </div>
    );
  };

  const doStepThree = async () => {
    closeModal();
    trackWebAppGAEvent('click', {
      category: IG_BETA_SIGNUP_GA_EVENT,
      label: `3_continue_button_click`,
    });
    await dispatch(
      facebookSDKActions.requestPermissionsWithManualFlowFallback(permissions),
    );
    if (facebookPage) {
      const res = await activateIG(facebookPage.id, false, skipFetchActivePage);
      if (res && res.status !== UNPROCESSABLE_ENTITY) {
        trackWebAppGAEvent('click', {
          category: trackEventName,
          label: `4_permissions_allowed`,
        });
        await chatbotCrudActions.retrieve(chatbotId);
        setTimeout(
          () =>
            modalActions.openInstagramConnectModal({
              startStep: 3,
              showCongratulations,
              onSuccessInstagramConnect,
              otherFacebookPage: facebookPage,
            }),
          100,
        );
      }
      if (res?.status === UNPROCESSABLE_ENTITY) {
        trackWebAppGAEvent('click', {
          category: trackEventName,
          label: `4_unprocessable_entity_result`,
        });
      }
    }
  };

  const StepFour = () => {
    return (
      <div className="instagram-connect-modal__step">
        <div className="instagram-connect-modal__step--intro">
          Thanks! We’ve received your beta submission. Your account will be
          submitted for approval.
        </div>
        <div className="instagram-connect-modal__step--text">
          You will receive an email notification after you’ve been approved. In
          the meantime, feel free to set up new Instagram automation under{' '}
          <b>
            Chatbot {'>'} Chat starters {'>'} Instagram
          </b>{' '}
          section. Your bot will start to work once your account has been
          approved for the beta.
        </div>
      </div>
    );
  };
  const CongratulationsStep = () => {
    return (
      <div className="instagram-connect-modal__step">
        <img
          src={instagramCrystal}
          className="instagram-connect-modal__img-ig"
        />
        <div className="instagram-connect-modal__step--header">
          Congratulations!
        </div>
        <div className="instagram-connect-modal__step--text">
          Your connection with Instagram was successful
        </div>
      </div>
    );
  };

  const stepFourObject = showCongratulations
    ? {
        body: <CongratulationsStep />,
        footer: 'Continue',
        onClick: () => {
          trackWebAppGAEvent('click', {
            category: trackEventName,
            label: `5_chat_starters_rsetup_click`,
          });
          closeModal();
          onSuccessInstagramConnect?.();
        },
        hideHeader: true,
        hideStepper: true,
      }
    : {
        body: <StepFour />,
        footer: 'Set up my Instagram Chat Starters',
        onClick: () => {
          trackWebAppGAEvent('click', {
            category: trackEventName,
            label: `5_chat_starters_rsetup_click`,
          });
          closeModal();
          const path = BotBuilderRoutes.chatStarters.getLink({
            chatbotId,
            kind: 'instagram_welcomer',
          });
          history.push(path);
        },
      };

  const steps: StepType[] = [
    {
      body: <StepOne />,
      footer: 'Confirmed! I’m ready for the next step',
      onClick: async () => {
        trackWebAppGAEvent('click', {
          category: trackEventName,
          label: `2_checkboxes_checked_confirmed_click`,
        });
        await retrieveBots();
        setStep(step + 1);
      },
      disabled: !allChecked,
    },
    {
      body: <StepTwo />,
      footer: 'Continue',
      onClick: doStepThree,
    },
    // Note: it's a placeholder needed just for the order of steps in the stepper
    {
      body: null,
      footer: null,
      onClick: () => {},
    },
    stepFourObject,
  ];

  return (
    <div className="instagram-connect-modal">
      <ModalHeader
        closeOverride={
          step === 3 && showCongratulations ? steps[step]?.onClick : closeModal
        }
        childrenContainerClassName="instagram-connect-modal__header"
      >
        {!steps[step]?.hideHeader && (
          <>
            <InstagramIcon className="instagram-connect-modal__header-icon" />
            Connect Instagram
          </>
        )}
      </ModalHeader>

      <ModalBody className="instagram-connect-modal__body">
        {!steps[step]?.hideStepper && (
          <StepperComponent
            size={4}
            activeStep={step + 1}
            css={{ container: 'instagram-connect-modal__stepper' }}
            isTransparent
          />
        )}
        {steps[step]?.body}
      </ModalBody>

      <ModalFooter className="instagram-connect-modal__footer">
        <PrimaryButton
          color={
            step === 3 && !showCongratulations
              ? ButtonColor.green
              : ButtonColor.blue
          }
          className="instagram-connect-modal__button"
          onClick={steps[step]?.onClick}
          disabled={steps[step]?.disabled}
        >
          {steps[step]?.footer}
          {step !== 3 ||
            (step === 3 && showCongratulations && (
              <i className="fas fa-chevron-right instagram-connect-modal__button-arrow" />
            ))}
        </PrimaryButton>
      </ModalFooter>
    </div>
  );
};

export default InstagramConnectModal;
